<template>
    <div class="flex justify-between bg-green-500 py-2 leg:py-3 !text-black">
        <div class="h-[14px] max-h-[14px] w-full xl:hidden">
            <!--
                Static first slide is rendered server-side; the rotation
                only kicks in once keen-slider has been dynamically loaded
                client-side (see `onMounted` below). Reserved height keeps
                layout stable while the slider is being set up.
            -->
            <div ref="container" class="keen-slider w-full" style="max-width: 100%">
                <div class="keen-slider__slide number-slide0">
                    <p class="text-center text-xs !text-black">
                        {{ $snippets['headless.header.freeShipping'] }}
                    </p>
                </div>
                <div :class="{ hidden: !isInitialized }" class="keen-slider__slide number-slide">
                    <p class="text-center text-xs">
                        {{ $snippets['headless.header.freeShippingDescription'] }}
                    </p>
                </div>
            </div>
        </div>
        <div class="normal-container relative hidden w-full justify-center leading-none xl:flex xl:justify-between">
            <div class="w-full md:flex md:w-max md:items-center">
                <div class="hidden !text-black xl:flex xl:items-center">
                    <p class="mr-8 font-semibold">
                        {{ $snippets['headless.header.freeShipping'] }}
                    </p>
                    <p>{{ $snippets['headless.header.freeShippingDescription'] }}</p>
                </div>
            </div>
            <div class="hidden xl:flex xl:items-center">
                <p class="mr-8">
                    <template v-if="todaysOpeningHours?.openingTime && todaysOpeningHours?.closingTime !== 'Closed'"
                        ><span class="font-semibold">{{ $snippets['headless.header.todayOpen'] }}</span> van
                        {{ todaysOpeningHours?.openingTime.replace(':', '.') }} tot
                        {{ todaysOpeningHours?.closingTime.replace(':', '.') }}
                    </template>
                    <template v-else>
                        <span class="font-bold">{{ $snippets['headless.header.todayClosed'] }}</span>
                    </template>
                </p>
                <NuxtLink :to="$snippets['headless.header.contactUrl']" class="mr-8 text-sm leading-none">{{
                    $snippets['headless.header.contactTitle']
                }}</NuxtLink>
                <a :href="$snippets['headless.header.customerReviewUrl']" target="_blank" class="flex items-end">
                    <p class="mr-2">
                        {{ $snippets['headless.header.customerReview'] }}
                    </p>
                    <StarRating />
                    <p class="ml-1">{{ $snippets['headless.review.rate'] }} / 10</p>
                </a>
            </div>
        </div>
    </div>
</template>
<script setup lang="ts">
    import StarRating from '~/components/StarRating.vue';

    const siteSettings = inject('siteSettings');

    const currentDate = new Date();
    const dayOfWeekName = currentDate.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();

    const todaysOpeningHours = ref({
        openingTime: siteSettings ? siteSettings[0]?.openingHours[dayOfWeekName]?.openingTime : '',
        closingTime: siteSettings ? siteSettings[0]?.openingHours[dayOfWeekName]?.closingTime : '',
    });

    const container = ref<HTMLElement | null>(null);
    const isInitialized = ref(false);
    let sliderInstance: { destroy?: () => void } | null = null;

    /**
     * keen-slider (~5.1 KiB gzipped) is loaded only after the initial render
     * via a dynamic import inside `onMounted`. The static first slide ships in
     * the SSR HTML, so users see content immediately; the rotation activates
     * once the slider module has been fetched on the client. This keeps
     * keen-slider out of the entry chunk on the LCP path.
     */
    onMounted(async () => {
        if (!container.value) {
            return;
        }
        const KeenSlider = await import('keen-slider');

        function UpdateSlider(slider: any) {
            const update = () => slider.update();
            slider.on('created', () => {
                const tabs = document.getElementById('productSliderTabs');
                if (!tabs) {
                    return;
                }
                tabs.addEventListener('click', update, { passive: false });
            });
        }

        function WheelControls(slider: any) {
            let touchTimeout: ReturnType<typeof setTimeout> | undefined;
            let position: { x: number; y: number } | undefined;
            let wheelActive = false;

            function dispatch(e: WheelEvent, name: string) {
                if (!position) {
                    return;
                }
                position.x -= e.deltaX;
                position.y -= e.deltaY;
                slider.container.dispatchEvent(
                    new CustomEvent(name, {
                        detail: { x: position.x, y: position.y },
                    }),
                );
            }

            function wheelStart(e: WheelEvent) {
                position = { x: e.pageX, y: e.pageY };
                dispatch(e, 'ksDragStart');
            }
            function wheel(e: WheelEvent) {
                dispatch(e, 'ksDrag');
            }
            function wheelEnd(e: WheelEvent) {
                dispatch(e, 'ksDragEnd');
            }

            function eventWheel(e: WheelEvent) {
                const isHorizontalScroll = Math.abs(e.deltaX) > Math.abs(e.deltaY);
                if (!isHorizontalScroll) {
                    return;
                }
                e.preventDefault();
                if (!wheelActive) {
                    wheelStart(e);
                    wheelActive = true;
                }
                wheel(e);
                clearTimeout(touchTimeout);
                touchTimeout = setTimeout(() => {
                    wheelActive = false;
                    wheelEnd(e);
                }, 50);
            }

            slider.on('created', () => {
                slider.container.addEventListener('wheel', eventWheel, { passive: false });
            });
        }

        sliderInstance = new KeenSlider.default(
            container.value,
            {
                created: () => {
                    isInitialized.value = true;
                },
                loop: true,
                rubberband: false,
                slides: { perView: 1 },
                mode: 'free-snap',
            },
            [
                WheelControls,
                UpdateSlider,
                (slider: any) => {
                    let timeout: ReturnType<typeof setTimeout> | undefined;
                    let mouseOver = false;
                    const clearNextTimeout = () => clearTimeout(timeout);
                    const nextTimeout = () => {
                        clearTimeout(timeout);
                        if (mouseOver) {
                            return;
                        }
                        timeout = setTimeout(() => slider.next(), 4000);
                    };
                    slider.on('created', () => {
                        slider.container.addEventListener('mouseover', () => {
                            mouseOver = true;
                            clearNextTimeout();
                        });
                        slider.container.addEventListener('mouseout', () => {
                            mouseOver = false;
                            nextTimeout();
                        });
                        nextTimeout();
                    });
                    slider.on('dragStarted', clearNextTimeout);
                    slider.on('animationEnded', nextTimeout);
                    slider.on('updated', nextTimeout);
                },
            ],
        );
    });

    onBeforeUnmount(() => {
        sliderInstance?.destroy?.();
        sliderInstance = null;
    });
</script>
<style scoped>
    @reference "tailwindcss";

    .group:hover .img-hover-filter {
        filter: invert(97%) sepia(6%) saturate(392%) hue-rotate(20deg) brightness(90%) contrast(88%);
    }

    body,
    p {
        @apply text-sm leading-none;
    }
    span.count-label {
        @apply absolute -top-3 -right-3 flex size-6 items-center justify-center tabular-nums;
        @apply rounded-full bg-green-400 pt-0.5 text-sm leading-[0] font-semibold text-black;
    }
</style>
